/**
 * The pixels behind the sun, the moon and the stars (T116, **D59**).
 *
 * 🔴 **Pure functions, and that is the whole point of the file.** This host has
 * no usable browser (**T111**) and there is no DOM test environment, so anything
 * that lives inside a three.js object is invisible to `pnpm gates`. **What can be
 * checked is the arithmetic**, so the arithmetic lives here on its own and
 * `skyobjects.ts` only hangs it on the scene.
 *
 * ⚠️ That split is not tidiness — it is the lesson from the HUD vanishing behind
 * the canvas on the day the status bar shipped: **a preview draws what you
 * intended; only a test can disagree with you.**
 */

/**
 * How big the disc textures are, in pixels.
 *
 * **64 is generous.** haugur chose ×8 for the moon, which is 4.24° across — about
 * **35 px** on a 620 px-tall view at the game's 75° field of view. A 64 px
 * texture is therefore already supersampling it, and the terminator stays smooth
 * when the disc is close to the camera's centre.
 */
export const DISC_TEXTURE_SIZE = 64;

/** Soft-edge width as a fraction of the radius, so the rim is not a staircase. */
const EDGE_SOFTNESS = 0.12;

/**
 * Draw a plain glowing disc — the sun.
 *
 * **White, with alpha falling to nothing just outside the rim.** The colour comes
 * from the material, not from here, so the same texture serves any light.
 */
export function writeDiscTexture(data: Uint8Array, size = DISC_TEXTURE_SIZE): void {
  const centre = (size - 1) / 2;
  const radius = size / 2;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const d = Math.hypot(x - centre, y - centre) / radius;
      const alpha = 1 - smooth(1 - EDGE_SOFTNESS, 1, d);
      write(data, (y * size + x) * 4, 255, 255, 255, Math.round(alpha * 255));
    }
  }
}

/**
 * Draw the moon, with the lit part of the disc showing and the rest transparent.
 *
 * 🔴 **The terminator is an ELLIPSE, not a straight line**, and that is the whole
 * difference between a moon and a pac-man. What you see is the edge of a sphere's
 * day side projected onto a circle: its half-width is `r · (1 − 2k)`, signed, so
 * it starts at the left rim when new, passes through **a straight line at exactly
 * half**, and reaches the right rim when full.
 *
 * ⚠️ **`phase`, not illumination.** They are different: illumination is the *area*
 * lit and is symmetric about full, while the phase says **which side** is lit.
 * Waxing and waning moons of equal brightness are mirror images, and using
 * illumination alone would light the wrong limb for half of every month.
 *
 * @param phase 0 and 1 are new, 0.5 is full. Waxing over the first half.
 */
export function writeMoonTexture(data: Uint8Array, phase: number, size = DISC_TEXTURE_SIZE): void {
  const centre = (size - 1) / 2;
  const radius = size / 2;

  /* Illuminated fraction, and which way round it is. */
  const k = (1 - Math.cos(2 * Math.PI * phase)) / 2;
  /* Waxing (0…0.5) is lit on the right; waning is lit on the left. */
  const waxing = phase <= 0.5;
  const terminatorHalfWidth = 1 - 2 * k;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const nx = (x - centre) / radius;
      const ny = (y - centre) / radius;
      const d = Math.hypot(nx, ny);

      let alpha = 1 - smooth(1 - EDGE_SOFTNESS, 1, d);
      if (alpha > 0) {
        /* Where the terminator crosses this row, as a signed x on the unit disc. */
        const cross = terminatorHalfWidth * Math.sqrt(Math.max(0, 1 - ny * ny));
        /* Mirror the whole test for a waning moon rather than writing it twice. */
        const along = waxing ? nx : -nx;
        /* Softened by one texel, so the terminator is not a hard jag. */
        alpha *= smooth(cross - 2 / size, cross + 2 / size, along);
      }

      write(data, (y * size + x) * 4, 255, 255, 255, Math.round(alpha * 255));
    }
  }
}

/**
 * How big the star dot texture is. Tiny — it is a few pixels on screen.
 */
export const STAR_TEXTURE_SIZE = 16;

/**
 * A soft round dot for a star.
 *
 * 🔴 **Both the colour AND the alpha fall off**, and that is the point. The star
 * quads are drawn **additively**, and additive blending adds the colour whatever
 * the alpha says — so a texture that faded only its alpha would still add a hard
 * white square. **The falloff has to be in the RGB or the star has corners.**
 *
 * 🎯 haugur's first sight of untextured quads: they read as confetti. **A star is
 * a point of light with no edge**; the edge is the entire difference.
 */
export function writeStarTexture(data: Uint8Array, size = STAR_TEXTURE_SIZE): void {
  const centre = (size - 1) / 2;
  const radius = size / 2;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const d = Math.hypot(x - centre, y - centre) / radius;
      /* Squared falloff: bright core, quick fade, nothing at the rim. A linear
         ramp reads as a fuzzy blob rather than as a point of light. */
      const fall = Math.max(0, 1 - d) ** 2;
      const v = Math.round(fall * 255);
      write(data, (y * size + x) * 4, v, v, v, v);
    }
  }
}

/**
 * Scatter stars over the **whole** sphere (**D59** — invented, not a catalogue).
 *
 * 🎯 **haugur:** *"they are not based on any fact data but a beautyful visual
 * thing to please the eye… the just have to be visable as white dots."*
 *
 * 🔴 **The FULL sphere, and the first version got this wrong.** It filled only
 * the upper hemisphere — stars below the horizon are behind the ground, so why
 * pay for them? — **and then the whole field is rotated about the celestial
 * pole.** The pole leans 64° from vertical, so as the sky turns, the empty lower
 * half sweeps up into view and leaves a bald patch. 🎯 haugur, within minutes:
 * *"there seem like half the sky is missing stars."*
 *
 * > **A field you intend to rotate has to be complete in every direction it can
 * > be rotated into — which, about a tilted axis, is all of them.**
 *
 * 🔴 **And the density is now UNIFORM, which is also a correction.** The first
 * version thinned the stars toward the horizon, to mimic looking through more
 * air. That thinning was baked in **at generation time**, so after the field
 * rotated it no longer had anything to do with altitude — it became a permanent
 * sparse patch travelling round the sky with the stars. **Real star density is
 * uniform on the celestial sphere; the apparent thinning is atmospheric and
 * belongs at draw time, keyed on where a star is NOW.** ⬜ Not built; a uniform
 * sky is honest without it.
 *
 * ✅ **Deterministic from `seed`**, so the sky is the same every time the game
 * loads. A star field that reshuffled on each visit would be quietly wrong in a
 * way nobody could name.
 *
 * @returns `xyz` triples on the unit sphere, and a brightness per star
 */
export function starField(
  count: number,
  seed = 20260811,
): { positions: Float32Array; brightness: Float32Array } {
  const random = mulberry(seed);
  const positions = new Float32Array(count * 3);
  const brightness = new Float32Array(count);

  for (let i = 0; i < count; i++) {
    /* ⚠️ `y` is uniform on −1…1, NOT the angle. Sampling the angle uniformly
       bunches stars at the poles — Archimedes' theorem is what stops that, and
       it is why there is no `acos` here. */
    const y = random() * 2 - 1;
    const ring = Math.sqrt(Math.max(0, 1 - y * y));
    const theta = random() * Math.PI * 2;

    positions[i * 3] = Math.cos(theta) * ring;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = Math.sin(theta) * ring;

    /* 🔴 **Mostly faint, a few bright** — and the bias was strengthened after the
       first sky read as a uniform field of confetti. **Cubed, from a low floor**:
       a real sky is a handful of obvious stars over a wash of ones you half see,
       and an even distribution is exactly what makes a star field look fake. */
    // eslint-disable-next-line security/detect-object-injection
    brightness[i] = 0.12 + 0.88 * random() ** 3;
  }

  return { positions, brightness };
}

function smooth(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function write(data: Uint8Array, p: number, r: number, g: number, b: number, a: number): void {
  /* eslint-disable security/detect-object-injection --
     `p` is derived from the loop bounds and the caller's buffer is allocated at
     exactly size × size × 4. No input reaches this. */
  data[p] = r;
  data[p + 1] = g;
  data[p + 2] = b;
  data[p + 3] = a;
  /* eslint-enable security/detect-object-injection */
}

/** A small deterministic generator — the same sky on every load. */
function mulberry(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
